import { useEffect, useState } from 'react';
import {
  fetchLinkedInAccounts,
  saveLinkedInAccounts,
  type LinkedInAdAccount,
} from '../../api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface LinkedInAccountSelectorProps {
  sessionId: string;
  onSaved: (count: number) => void;
  onClose: () => void;
}

/** Badge for the non-obvious ad-account states LinkedIn reports. */
const getStatusBadge = (status: string | null) => {
  switch (status) {
    case 'DRAFT':
      return { variant: 'secondary' as const, label: 'Draft' };
    case 'CANCELED':
    case 'CANCELLED':
      return { variant: 'destructive' as const, label: 'Cancelled' };
    default:
      return null; // ACTIVE (and anything unknown) needs no badge
  }
};

/**
 * LinkedIn Ads connect step: pick which sponsored ad accounts the audit may
 * read. One step only — unlike Google Ads there is no manager hierarchy, and
 * LinkedIn reports every metric in UTC with no per-account timezone.
 */
export default function LinkedInAccountSelector({
  sessionId, onSaved, onClose,
}: LinkedInAccountSelectorProps) {
  const [accounts, setAccounts] = useState<LinkedInAdAccount[]>([]);
  const [memberEmail, setMemberEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchLinkedInAccounts(sessionId)
      .then((response) => {
        if (cancelled) return;
        setAccounts(response.accounts || []);
        setMemberEmail(response.memberEmail);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFetchError(
          err instanceof Error ? err.message : 'Failed to load ad accounts. Please try again.',
        );
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  const toggle = (adAccountId: string) => {
    setSelectedIds((prev) =>
      prev.includes(adAccountId)
        ? prev.filter((id) => id !== adAccountId)
        : [...prev, adAccountId],
    );
  };

  const toggleAll = () => {
    setSelectedIds(
      selectedIds.length === accounts.length ? [] : accounts.map((a) => a.ad_account_id),
    );
  };

  const handleConfirm = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const result = await saveLinkedInAccounts({
        session_id: sessionId,
        selected_accounts: accounts.filter((a) => selectedIds.includes(a.ad_account_id)),
      });
      onSaved(result.count);
      onClose();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save LinkedIn accounts');
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img
              src="/logos/linkedIn_logo.svg"
              alt="LinkedIn Ads"
              className="size-8 shrink-0 rounded object-contain"
            />
            <div>
              <DialogTitle>Select Ad Accounts</DialogTitle>
              <DialogDescription>
                {memberEmail
                  ? `Connected as ${memberEmail}`
                  : 'Choose which LinkedIn ad accounts to analyze'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mb-3 size-8 animate-spin text-foreground" />
              <p className="text-sm">Loading LinkedIn ad accounts...</p>
            </div>
          ) : fetchError ? (
            <div className="py-8 text-center">
              <p className="mb-2 text-sm text-destructive">{fetchError}</p>
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-foreground hover:text-muted-foreground"
              >
                Close
              </button>
            </div>
          ) : accounts.length === 0 ? (
            <p className="rounded-lg bg-muted/50 py-4 text-center text-sm text-muted-foreground">
              No LinkedIn ad accounts found for this member
            </p>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Ad Accounts ({accounts.length})</h3>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs font-medium text-foreground hover:text-muted-foreground"
                >
                  {selectedIds.length === accounts.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-2">
                {accounts.map((account) => {
                  const statusBadge = getStatusBadge(account.account_status);
                  const isSelected = selectedIds.includes(account.ad_account_id);
                  return (
                    <label
                      key={account.ad_account_id}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(account.ad_account_id)}
                        className="size-4 shrink-0 accent-primary"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium">{account.account_name}</p>
                          {statusBadge && (
                            <Badge
                              variant={statusBadge.variant}
                              className="px-1.5 py-0 text-[10px]"
                            >
                              {statusBadge.label}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>ID: {account.ad_account_id}</span>
                          {account.currency && (
                            <>
                              <span>·</span>
                              <span>{account.currency}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}

        {!loading && !fetchError && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <p className="text-sm text-muted-foreground">
              {selectedIds.length} account{selectedIds.length !== 1 ? 's' : ''} selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void handleConfirm()} disabled={!selectedIds.length || saving}>
                {saving ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
